import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import InputForm from './components/InputForm';
import ResultsPanel from './components/ResultsPanel';
import VisualizationPanel from './components/VisualizationPanel';
import PathwaysPanel from './components/PathwaysPanel';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import { compileCenterResults } from './utils/newApi';
import { loadTransplantCenters, getCenterNameById, loadSummaryData, getSummaryData, getCenterPerformanceY1, isIOTACenter, centerExists } from './utils/formatters';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  
  const [inputs, setInputs] = useState({
    centerName: '',
    centerId: '',
    organType: 'Kidney',
    numTransplants: 0,
    offerAcceptRate: 0,
    graftSurvival: 0,
    medicareFfsPercent: 41,
    livingDonorPercent: 0,
    apiUrl: '',
    mode: 'mock'
  });

  const [response, setResponse] = useState(null);
  const [status, setStatus] = useState('idle');
  const [showDetails, setShowDetails] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  const [showSwitchTooltip, setShowSwitchTooltip] = useState(false);
  const autoRunTimer = useRef(null);

  // Load transplant centers and summary data on mount
  useEffect(() => {
    loadTransplantCenters();
    loadSummaryData();
  }, []);

  // Check if user is on mobile device
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
        || window.innerWidth <= 640;
      
      // Only show warning on actual mobile devices, not just small screens
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      if (isMobile && isTouchDevice) {
        setShowMobileWarning(true);
      }
    };
    
    checkMobile();
  }, []);

  const handleInputChange = (field, value) => {
    setInputs(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-fill center name and performance Y1 data when center ID changes
      if (field === 'centerId') {
        if (value) {
          const centerName = getCenterNameById(value);
          if (centerName) {
            updated.centerName = centerName;
          } else {
            updated.centerName = '';
          }
          
          // Only check and show warnings if the center ID is exactly 4 characters
          if (value.length === 4) {
            const exists = centerExists(value);
            const isIOTA = isIOTACenter(value);
            
            if (!exists) {
              setWarningMessage("We don't have any baseline data for this center");
            } else if (!isIOTA) {
              setWarningMessage("This center is not participating in IOTA");
            } else {
              setWarningMessage('');
            }
          } else {
            setWarningMessage('');
          }
        } else {
          updated.centerName = '';
          setWarningMessage('');
        }
        
        // Load Performance Y1 data for this center
        const perfData = getCenterPerformanceY1(value);
        if (perfData) {
          if (perfData.numTransplants !== null) {
            updated.numTransplants = perfData.numTransplants;
          }
          if (perfData.offerAcceptRate !== null) {
            updated.offerAcceptRate = perfData.offerAcceptRate;
          }
          if (perfData.graftSurvival !== null) {
            updated.graftSurvival = Math.round(perfData.graftSurvival * 10) / 10;
          }
          if (perfData.livingDonorPercent !== null) {
            updated.livingDonorPercent = Math.round(perfData.livingDonorPercent * 10) / 10;
          }
        }
      }
      
      return updated;
    });
  };

  const handleLoadExample = () => {
    setWarningMessage('');
    setInputs({
      centerName: 'University of California San Francisco Medical Center',
      centerId: 'CASF',
      organType: 'Kidney',
      numTransplants: 361,
      offerAcceptRate: 1.53,
      graftSurvival: 95.40,
      medicareFfsPercent: 41,
      livingDonorPercent: 35.7,
      apiUrl: '',
      mode: 'mock'
    });
    
    // Track example load in PostHog
    if (window.posthog) {
      window.posthog.capture('example_loaded');
    }
  };

  const handleReset = () => {
    setWarningMessage('');
    setInputs({
      centerName: '',
      centerId: 'CASF',
      organType: 'Kidney',
      numTransplants: 361,
      offerAcceptRate: 1.53,
      graftSurvival: 95.40,
      medicareFfsPercent: 41,
      livingDonorPercent: 35.7,
      apiUrl: '',
      mode: 'mock'
    });
  };

  const handleSwitchCenter = () => {
    setWarningMessage('');
    setResponse(null);
    setStatus('idle');
    setInputs(prev => ({
      ...prev,
      centerName: '',
      centerId: '',
      numTransplants: 0,
      offerAcceptRate: 0,
      graftSurvival: 0,
      livingDonorPercent: 0
    }));
    
    // Track center switch in PostHog
    if (window.posthog) {
      window.posthog.capture('switch_center_clicked', {
        previous_center_id: inputs.centerId,
        previous_center_name: inputs.centerName
      });
    }
  };

  const handleCalculate = useCallback(async () => {
    setStatus('running');
    try {
      let result = null;
      if (inputs.mode === 'mock') {
        // Simulate delay
        await new Promise(resolve => setTimeout(resolve, 700 + Math.random() * 500));
        
        // Get the summary data and add it to inputs
        const summaryData = getSummaryData();
        const enrichedInputs = {
          ...inputs,
          summary: summaryData,
          graftData: summaryData, // Using same data for graft calculations
          centerCode: inputs.centerId
        };
        
        result = compileCenterResults(enrichedInputs);
        console.log("RESULT", result);
      } else {
        // Call live API
        const url = inputs.apiUrl;
        if (!url) throw new Error('API URL required when running in live mode.');
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ inputs }),
          mode: 'cors'
        });
        if (!res.ok) throw new Error(`API request failed (${res.status})`);
        result = await res.json();
      }
      setResponse(result);
      setStatus('success');
      
      // Track calculator run in PostHog
      if (window.posthog) {
        window.posthog.capture('calculator_run', {
          center_id: inputs.centerId,
          center_name: inputs.centerName,
          num_transplants: inputs.numTransplants,
          offer_accept_rate: inputs.offerAcceptRate,
          graft_survival: inputs.graftSurvival,
          mode: inputs.mode
        });
      }
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  }, [inputs]);

  // Auto-run model when required fields are filled
  useEffect(() => {
    const hasRequiredFields = 
      inputs.numTransplants && 
      inputs.offerAcceptRate && 
      inputs.graftSurvival &&
      inputs.numTransplants !== '' &&
      inputs.offerAcceptRate !== '' &&
      inputs.graftSurvival !== '';
    
    if (hasRequiredFields) {
      // Clear any existing timer
      if (autoRunTimer.current) {
        clearTimeout(autoRunTimer.current);
      }
      
      // Use a small delay to debounce rapid changes (especially for slider)
      autoRunTimer.current = setTimeout(() => {
        handleCalculate();
      }, 300);
      
      return () => {
        if (autoRunTimer.current) {
          clearTimeout(autoRunTimer.current);
        }
      };
    }
  }, [inputs.numTransplants, inputs.offerAcceptRate, inputs.graftSurvival, inputs.medicareFfsPercent, inputs.livingDonorPercent, handleCalculate]);

  const handleCopyJson = () => {
    if (!response) return alert('No response to copy.');
    navigator.clipboard.writeText(JSON.stringify(response, null, 2)).then(() => {
      alert('Copied to clipboard!');
    }).catch(() => alert('Copy failed.'));
  };

  const handleDownloadCsv = () => {
    if (!response) return alert('No results to download.');
    const payments = response.payments || [];
    let csv = 'Transplant ID,Upside,Downside\n';
    payments.forEach(p => {
      csv += `${p.id || ''},${p.upside || 0},${p.downside || 0}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'iota_payments.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {isLoading ? (
        <LoadingScreen onLoadingComplete={() => setIsLoading(false)} />
      ) : (
        <AnimatePresence mode="wait">
          {!inputs.centerId ? (
            // Centered view before center selection
            <motion.div 
              key="center-selection"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ 
                duration: 0.4,
                ease: [0.4, 0, 0.2, 1]
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 'calc(100vh - 200px)',
                padding: '40px 20px'
              }}
            >
              <Header 
                onLoadExample={handleLoadExample}
                inputs={inputs}
              />
              <motion.div 
                style={{
                  maxWidth: '600px',
                  width: '100%',
                  marginTop: '60px'
                }}
              >
                <h2 style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#0e4f56',
                  textAlign: 'center',
                  marginBottom: '12px'
                }}>
                  IOTA Calculator
                </h2>
                <p style={{
                  fontSize: '16px',
                  color: 'rgba(0,0,0,0.6)',
                  textAlign: 'center',
                  marginBottom: '24px',
                  lineHeight: '1.5'
                }}>
                  Select your transplant center to get started
                </p>
                <InputForm 
                  inputs={inputs}
                  onInputChange={handleInputChange}
                  onCalculate={handleCalculate}
                  onReset={handleReset}
                  warningMessage={warningMessage}
                  response={response}
                  centerOnly={true}
                />
              </motion.div>
            </motion.div>
          ) : (
            // Grid view after center selection
            <>
              <motion.div
                key="center-results-header"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.5,
                  ease: [0.4, 0, 0.2, 1],
                  delay: 0.1
                }}
                style={{ 
                  width: '100%',
                  maxWidth: '1400px',
                  margin: '0 auto'
                }}
              >
                <Header 
                  onLoadExample={handleLoadExample}
                  inputs={inputs}
                />
              </motion.div>

              {/* Full-width Center Name Title */}
              <motion.div 
                style={{
                  width: '100%',
                  marginTop: '32px',
                  marginBottom: '24px',
                  borderBottom: '2px solid rgba(14,79,86,0.1)',
                  paddingBottom: '16px'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.5,
                  delay: 0.15,
                  ease: [0.4, 0, 0.2, 1]
                }}
              >
                <div style={{
                  maxWidth: '1400px',
                  margin: '0 auto'
                }}>
                  {/* Title Row with Button */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    gap: '16px',
                    marginBottom: '8px'
                  }}>
                    <h2 style={{
                      fontSize: '42px',
                      fontWeight: '700',
                      color: '#0e4f56',
                      margin: '0',
                      lineHeight: '1.2',
                      flex: 1
                    }}>
                      {inputs.centerName || 'Transplant Center'}
                    </h2>
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={handleSwitchCenter}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          padding: '0',
                          backgroundColor: 'transparent',
                          color: '#0e4f56',
                          border: '2px solid #0e4f56',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          setShowSwitchTooltip(true);
                          e.currentTarget.style.backgroundColor = '#0e4f56';
                          e.currentTarget.style.transform = 'rotate(180deg)';
                          const svg = e.currentTarget.querySelector('svg');
                          if (svg) svg.style.stroke = '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                          setShowSwitchTooltip(false);
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.transform = 'rotate(0deg)';
                          const svg = e.currentTarget.querySelector('svg');
                          if (svg) svg.style.stroke = '#0e4f56';
                        }}
                      >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transition: 'stroke 0.2s ease' }}>
                          <path d="M1 4v6h6M23 20v-6h-6" stroke="#0e4f56" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="#0e4f56" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      {showSwitchTooltip && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          right: '0',
                          marginTop: '8px',
                          backgroundColor: '#2a2a2a',
                          color: '#ffffff',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                          zIndex: 1000,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                          pointerEvents: 'none'
                        }}>
                          Switch Center
                          <div style={{
                            position: 'absolute',
                            bottom: '100%',
                            right: '12px',
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderBottom: '6px solid #2a2a2a'
                          }} />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Center ID and Warning */}
                  <div>
                    <div style={{
                      fontSize: '15px',
                      color: 'rgba(0,0,0,0.6)',
                      fontWeight: '600',
                      letterSpacing: '0.05em'
                    }}>
                      Center ID: {inputs.centerId}
                    </div>
                    {warningMessage && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#dc2626',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        ⚠️ {warningMessage}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                key="center-results-content"
                className="container"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ 
                  duration: 0.5,
                  ease: [0.4, 0, 0.2, 1],
                  delay: 0.2
                }}
              >

              {/* Top Section: Parameters and Scores */}
              <motion.div
                style={{ 
                  gridColumn: '1 / -1', 
                  display: 'grid', 
                  gap: '0', 
                  alignItems: 'start',
                  gridTemplateColumns: '500px 1px 1fr',
                  position: 'relative'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: 1, 
                  y: 0
                }}
                transition={{ 
                  opacity: { duration: 0.5, delay: 0.2 },
                  y: { duration: 0.5, delay: 0.2, ease: [0.4, 0, 0.2, 1] }
                }}
              >
                {/* Parameters Section - Left Side */}
                <section 
                  aria-label="Adjustable Parameters"
                  style={{ 
                    margin: '0',
                    paddingRight: '10px'
                  }}
                >
                  <InputForm 
                    inputs={inputs}
                    onInputChange={handleInputChange}
                    onCalculate={handleCalculate}
                    onReset={handleReset}
                    warningMessage={warningMessage}
                    response={response}
                    centerOnly={false}
                  />
                </section>

                {/* Vertical Divider */}
                <div style={{
                  width: '1px',
                  background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.1) 20%, rgba(0,0,0,0.1) 80%, transparent)',
                  alignSelf: 'stretch',
                  margin: '0 10px 0 0'
                }} />

                {/* Scores Section - Right Side (no card) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingLeft: '32px' }}>
                  <ResultsPanel 
                    response={response}
                    status={status}
                    showDetails={showDetails}
                    onToggleDetails={() => setShowDetails(!showDetails)}
                    onCopyJson={handleCopyJson}
                    onDownloadCsv={handleDownloadCsv}
                    inputs={inputs}
                  />
                </div>
              </motion.div>

              <motion.div 
                className="visualization-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.5,
                  delay: 0.3,
                  ease: [0.4, 0, 0.2, 1]
                }}
                style={{ gridColumn: '1 / -1' }}
              >
                <VisualizationPanel 
                  response={response}
                  inputs={inputs}
                />
              </motion.div>

              {/* Horizontal divider */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ 
                  duration: 0.5,
                  delay: 0.35,
                  ease: [0.4, 0, 0.2, 1]
                }}
                style={{ 
                  gridColumn: '1 / -1',
                  height: '1px',
                  background: 'linear-gradient(90deg, transparent, rgba(14,79,86,0.2) 20%, rgba(14,79,86,0.2) 80%, transparent)',
                  margin: '32px 0'
                }}
              />

              <motion.div 
                className="pathways-section" 
                style={{ gridColumn: '1 / -1' }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.5,
                  delay: 0.4,
                  ease: [0.4, 0, 0.2, 1]
                }}
              >
                <PathwaysPanel 
                  response={response}
                  inputs={inputs}
                />
              </motion.div>
            </motion.div>
            </>
          )}
        </AnimatePresence>
      )}
      
      <Footer />
      
      {/* Mobile Warning Popup */}
      <AnimatePresence>
        {showMobileWarning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '20px'
            }}
            onClick={() => setShowMobileWarning(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                padding: '32px 24px',
                maxWidth: '400px',
                width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                textAlign: 'center'
              }}
            >
              <div style={{
                width: '64px',
                height: '64px',
                margin: '0 auto 20px',
                backgroundColor: '#0e4f56',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="2" width="16" height="20" rx="2" stroke="white" strokeWidth="2"/>
                  <path d="M4 18h16M12 18v2" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              
              <h2 style={{
                margin: '0 0 12px 0',
                fontSize: '22px',
                fontWeight: '700',
                color: '#0e4f56'
              }}>
                Best Viewed on Desktop
              </h2>
              
              <p style={{
                margin: '0 0 24px 0',
                fontSize: '15px',
                lineHeight: '1.6',
                color: 'rgba(0,0,0,0.7)'
              }}>
                The IOTA Calculator is optimized for desktop viewing. For the best experience, please visit this page on a computer.
              </p>
              
              <button
                onClick={() => setShowMobileWarning(false)}
                style={{
                  backgroundColor: '#0e4f56',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '14px 32px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.2s'
                }}
                onMouseDown={(e) => e.target.style.backgroundColor = '#0a3a3f'}
                onMouseUp={(e) => e.target.style.backgroundColor = '#0e4f56'}
              >
                Continue Anyway
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
    </>
  );
}

export default App;
